import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Set the base URL for your Node.js backend
const API_BASE_URL = 'https://kigaawater.inkenya.co.ke/api';
const LABOR_CHARGE = 50.00; // Fixed labor charge

function NewUserAccountForm() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState(null); // New state for success/error messages

  const [userDetails, setUserDetails] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
  });

  const [accountDetails, setAccountDetails] = useState({
    blockId: '',
    accountNumber: '',
    monthlyRate: 0.00,
    installationAddress: '',
    isActive: true,
    selectedBlockFee: 0.00, // For display
  });

  // 1. Fetch Blocks on component load
  useEffect(() => {
    const fetchBlocks = async () => {
      try {
        setLoading(true);
        // API call to GET /api/blocks
        const response = await axios.get(`https://kigaawater.inkenya.co.ke/api/blocks`);
        if (response.data.length === 0) {
          setError("No service blocks available. Please add blocks to the database.");
        } else {
          setBlocks(response.data);
        }
      } catch (err) {
        console.error("Failed to fetch blocks:", err);
        let errorMessage = "Failed to load service blocks. Check backend connection.";
        if (err.response) {
          if (err.response.status === 404) {
            errorMessage = "Blocks endpoint not found. Check backend setup.";
          } else if (err.response.status === 500) {
            errorMessage = "Backend server error. Check server logs.";
          } else {
            errorMessage = err.response.data.error || errorMessage;
          }
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    fetchBlocks();
  }, []);

  // 2. Handle input changes
  const handleUserChange = (e) => {
    setUserDetails({ ...userDetails, [e.target.name]: e.target.value });
  };

  const handleAccountChange = (e) => {
    const { name, value } = e.target;

    if (name === 'blockId') {
      // Find the fee for the selected block
      const selectedBlock = blocks.find(b => b.block_id.toString() === value);
      const fee = selectedBlock ? parseFloat(selectedBlock.connection_fee) : 0.00;

      setAccountDetails(prev => ({
        ...prev,
        blockId: value,
        selectedBlockFee: fee // Update fee state
      }));
    } else {
      setAccountDetails({ ...accountDetails, [name]: value });
    }
  };

  // Function to reset the form after successful submission
  const resetForm = () => {
    setUserDetails({ firstName: '', lastName: '', phoneNumber: '', email: '' });
    setAccountDetails({ blockId: '', accountNumber: '', monthlyRate: 0.00, installationAddress: '', isActive: true, selectedBlockFee: 0.00 });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmissionStatus({ message: 'Saving customer...', type: 'info' });

    const payload = {
      user: userDetails,
      account: {
        blockId: accountDetails.blockId,
        accountNumber: accountDetails.accountNumber,
        monthlyRate: parseFloat(accountDetails.monthlyRate) || 0.00,
        installationAddress: accountDetails.installationAddress,
        isActive: accountDetails.isActive,
      }
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/users-accounts`, payload);
      setSubmissionStatus({ message: 'Success! ' + response.data.message, type: 'success' });
      resetForm();
    } catch (err) {
      console.error("Submission Error:", err.response ? err.response.data : err);
      const errorMessage = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : 'Network Error or Server is Down.';
      setSubmissionStatus({ message: 'Error: ' + errorMessage, type: 'error' });
    }
  };

  // Render Loading/Error States
  if (loading) return <div style={styles.centerMessage}>Loading Blocks...</div>;
  if (error) return <div style={styles.centerMessage}>Error: {error}</div>;

  const totalInitialInvoice = accountDetails.selectedBlockFee + LABOR_CHARGE;

  // Render the form
  return (
    <div style={styles.container}>
      <form style={styles.form} onSubmit={handleSubmit}>
        {submissionStatus && (
          <div style={{
            ...styles.statusBox,
            backgroundColor: submissionStatus.type === 'success' ? '#d4edda' : submissionStatus.type === 'error' ? '#f8d7da' : '#e7f3ff',
            color: submissionStatus.type === 'success' ? '#155724' : submissionStatus.type === 'error' ? '#721c24' : '#004085',
          }}>
            {submissionStatus.message}
          </div>
        )}
        <h2 style={styles.header}>New Customer & Account</h2>
        <hr style={styles.hr} />
        <h3 style={{ marginBottom: '15px' }}>Customer Details</h3>
        <div style={styles.inputGroup}>
          <label style={styles.label}>First Name *</label>
          <input style={styles.input} type="text" name="firstName" value={userDetails.firstName} onChange={handleUserChange} required />
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label}>Last Name *</label>
          <input style={styles.input} type="text" name="lastName" value={userDetails.lastName} onChange={handleUserChange} required />
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label}>Phone Number *</label>
          <input style={styles.input} type="text" name="phoneNumber" value={userDetails.phoneNumber} onChange={handleUserChange} required />
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label}>Email</label>
          <input style={styles.input} type="email" name="email" value={userDetails.email} onChange={handleUserChange} />
        </div>
        <hr style={styles.hr} />
        <h3 style={{ marginBottom: '15px' }}>Account Details</h3>
        <div style={styles.inputGroup}>
          <label style={styles.label}>Service Block *</label>
          <select style={styles.input} name="blockId" value={accountDetails.blockId} onChange={handleAccountChange} required>
            <option value="">Select a Block</option>
            {blocks.map(block => (
              <option key={block.block_id} value={block.block_id}>{block.block_name} (Ksh {block.connection_fee})</option>
            ))}
          </select>
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label}>Account Number *</label>
          <input style={styles.input} type="text" name="accountNumber" value={accountDetails.accountNumber} onChange={handleAccountChange} required />
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label}>Monthly Rate (Ksh) *</label>
          <input style={styles.input} type="number" name="monthlyRate" value={accountDetails.monthlyRate} onChange={handleAccountChange} min="0.00" step="0.01" required />
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label}>Installation Address *</label>
          <textarea style={{ ...styles.input, height: '80px' }} name="installationAddress" value={accountDetails.installationAddress} onChange={handleAccountChange} required />
        </div>
        <div style={styles.checkboxGroup}>
          <input style={styles.checkbox} type="checkbox" name="isActive" id="isActive" checked={accountDetails.isActive} onChange={() => setAccountDetails({ ...accountDetails, isActive: !accountDetails.isActive })} />
          <label htmlFor="isActive" style={styles.label}>Account is Active</label>
        </div>
        <hr style={styles.hr} />
        <div style={styles.summaryBox}>
          <h3 style={{ marginBottom: '10px' }}>Initial Charges Summary</h3>
          <p style={styles.summaryItem}>Connection Fee: <span style={styles.summaryValue}>Ksh {accountDetails.selectedBlockFee.toFixed(2)}</span></p>
          <p style={styles.summaryItem}>Labor Charge: <span style={styles.summaryValue}>Ksh {LABOR_CHARGE.toFixed(2)}</span></p>
          <p style={{ ...styles.summaryItem, fontWeight: 'bold', borderTop: '1px dashed #ccc', paddingTop: '8px', marginTop: '5px' }}>
            Total Initial Invoice: <span style={styles.summaryValue}>Ksh {totalInitialInvoice.toFixed(2)}</span>
          </p>
        </div>
        <button type="submit" style={styles.button}>Save Customer & Account</button>
      </form>
    </div>
  );
}

// Styling Object
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: '#f4f7f6',
  },
  form: {
    width: '100%',
    maxWidth: '600px',
    padding: '30px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
    fontFamily: 'Arial, sans-serif',
  },
  header: {
    textAlign: 'center',
    color: '#007bff',
    marginBottom: '5px',
  },
  hr: {
    border: '0',
    height: '1px',
    backgroundColor: '#eee',
    margin: '20px 0',
  },
  inputGroup: {
    marginBottom: '15px',
  },
  label: {
    display: 'block',
    marginBottom: '5px',
    fontWeight: 'bold',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    boxSizing: 'border-box',
    fontSize: '16px',
    transition: 'border-color 0.3s',
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  checkbox: {
    marginRight: '10px',
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  summaryBox: {
    padding: '15px',
    backgroundColor: '#f0faff',
    border: '1px solid #b3e0ff',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  summaryItem: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '1.1em',
    color: '#555',
    marginBottom: '5px',
  },
  summaryValue: {
    fontWeight: 'normal',
    color: '#000',
  },
  button: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '18px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'background-color 0.3s',
  },
  statusBox: {
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '20px',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  centerMessage: {
    textAlign: 'center',
    padding: '50px',
    fontSize: '1.2em',
  }
};

export default NewUserAccountForm;